if (!customElements.get('product-form')) {
  const CT_ADAPTER_ADDON_SELECTOR = '[data-ct-adapter-addon]';
  const CT_ADAPTER_OPTION_SELECTOR = '[data-ct-adapter-option]';

  const ctCssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  };

  const getRootUrl = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getFormQuantity = (form) => {
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? Number(quantityInput.value || 1) : 1;
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getAdapterSelection = (form) => {
    const formId = form ? form.getAttribute('id') : '';
    if (!formId) return null;

    const root = document.querySelector(`${CT_ADAPTER_ADDON_SELECTOR}[data-form-id="${ctCssEscape(formId)}"]`);
    if (!root) return null;

    const selectedOption = root.querySelector(`${CT_ADAPTER_OPTION_SELECTOR}:checked`);
    if (!selectedOption || selectedOption.value === 'none') return null;

    const variantId = Number(selectedOption.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    const quantityMode = root.dataset.quantityMode || 'one';
    const quantity = quantityMode === 'match' ? getFormQuantity(form) : 1;

    return {
      root,
      variantId,
      quantity,
      discountCode: (root.dataset.discountCode || '').trim(),
    };
  };

  const getPropertiesFromFormData = (formData) => {
    const properties = {};

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    });

    return Object.keys(properties).length ? properties : null;
  };

  const buildCartPayload = (form, cart) => {
    const formData = new FormData(form);
    const adapterSelection = getAdapterSelection(form);

    if (!adapterSelection) return { formData, payload: null, adapterSelection: null };

    const productVariantId = Number(formData.get('id'));
    if (!Number.isFinite(productVariantId) || productVariantId <= 0) {
      return { formData, payload: null, adapterSelection: null };
    }

    const productItem = {
      id: productVariantId,
      quantity: getFormQuantity(form),
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) productItem.selling_plan = sellingPlan;

    const properties = getPropertiesFromFormData(formData);
    if (properties) productItem.properties = properties;

    const adapterItem = {
      id: adapterSelection.variantId,
      quantity: adapterSelection.quantity,
      properties: {
        _ct_addon: 'adapter',
        _ct_parent_variant: String(productVariantId),
      },
    };

    const payload = {
      items: [productItem, adapterItem],
    };

    if (cart) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
    }

    return { formData, payload, adapterSelection };
  };

  const normalizeCartAddResponse = (response) => {
    if (response && !response.key && Array.isArray(response.items) && response.items[0] && response.items[0].key) {
      response.key = response.items[0].key;
    }

    return response;
  };

  const applyCartDiscount = (discountCode, cart) => {
    const body = {
      discount: discountCode,
    };

    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    return fetch(`${getRootUrl()}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then(normalizeCartAddResponse);
  };

  const buildCartPermalink = (payload, adapterSelection) => {
    const cartLines = payload.items.map((item) => `${item.id}:${item.quantity}`).join(',');
    const params = new URLSearchParams();
    if (adapterSelection.discountCode) params.set('discount', adapterSelection.discountCode);
    const queryString = params.toString();

    return `${getRootUrl()}cart/${cartLines}${queryString ? `?${queryString}` : ''}`;
  };

  const setDirectCheckoutLoading = (button, form) => {
    if (form && form.dataset.ctCheckoutPending === 'true') return false;
    if (form) form.dataset.ctCheckoutPending = 'true';

    if (button.dataset.ctDirectBuyWasDisabled === undefined) {
      button.dataset.ctDirectBuyWasDisabled = button.disabled ? 'true' : 'false';
      button.dataset.ctDirectBuyAriaDisabled = button.hasAttribute('aria-disabled')
        ? button.getAttribute('aria-disabled') || ''
        : '__missing__';
    }
    button.dataset.ctDirectBuyPending = 'true';
    if (button.ctDirectBuyOriginalMarkup === undefined) {
      button.ctDirectBuyOriginalMarkup = button.innerHTML;
    }
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('ct-adapter-checkout-loading');
    button.textContent = 'Preparando tu checkout...';

    try {
      document.dispatchEvent(new CustomEvent('ct:direct-buy-loading', { detail: { button, form } }));
    } catch (error) {}

    return true;
  };

  const handleAdapterDirectCheckout = (evt) => {
    const button = evt.target.closest('.shopify-payment-button__button--unbranded');
    if (!button) return;

    const productForm = button.closest('product-form');
    const form = productForm ? productForm.querySelector('form') : null;
    if (!form) return;

    const { payload, adapterSelection } = buildCartPayload(form, null);
    if (!payload || !adapterSelection) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    if (!setDirectCheckoutLoading(button, form)) return;

    try {
      window.location.assign(buildCartPermalink(payload, adapterSelection));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('ct:direct-buy-error', { detail: { button, form } }));

      if (button.dataset.ctDirectBuyPending === 'true') {
        delete button.dataset.ctDirectBuyPending;
        delete form.dataset.ctCheckoutPending;
        button.disabled = button.dataset.ctDirectBuyWasDisabled === 'true';
        if (button.dataset.ctDirectBuyAriaDisabled === '__missing__') button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', button.dataset.ctDirectBuyAriaDisabled || 'false');
        delete button.dataset.ctDirectBuyWasDisabled;
        delete button.dataset.ctDirectBuyAriaDisabled;
        button.removeAttribute('aria-busy');
        button.classList.remove('ct-adapter-checkout-loading');
        if (button.ctDirectBuyOriginalMarkup !== undefined) {
          button.innerHTML = button.ctDirectBuyOriginalMarkup;
          delete button.ctDirectBuyOriginalMarkup;
        }
      }
    }
  };
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const { formData, payload, adapterSelection } = buildCartPayload(this.form, this.cart);
        if (payload) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(normalizeCartAddResponse)
          .then((response) => {
            if (response.status || !adapterSelection || !adapterSelection.discountCode) return response;

            return applyCartDiscount(adapterSelection.discountCode, this.cart).catch((error) => {
              console.error(error);
              return response;
            });
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );

  document.addEventListener('click', handleAdapterDirectCheckout, true);
}



